import { activeProfile, LEGACY } from '../store/profile';

// Stores files added from this device in IndexedDB so they survive reloads. They never
// leave the device: settings sync strips local items before upload. Keys are namespaced
// by profile ("<profile>/<id>") so each account only sees – and only prunes – its own.

const DB_NAME = 'kcl-canvas-media';
const STORE = 'files';

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (!('indexedDB' in window)) return Promise.reject(new Error('IndexedDB unavailable'));
  dbPromise ??= new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => {
      dbPromise = null;
      reject(req.error ?? new Error('IndexedDB open failed'));
    };
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = run(t.objectStore(STORE));
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error ?? new Error('IndexedDB transaction failed'));
        t.onabort = () => reject(t.error ?? new Error('IndexedDB transaction aborted'));
      }),
  );
}

const keyFor = (id: string) => `${activeProfile()}/${id}`;

export async function saveLocalFile(id: string, file: Blob): Promise<void> {
  await tx('readwrite', (s) => s.put(file, keyFor(id)));
  // Ask the browser not to evict our files under storage pressure (best effort).
  void navigator.storage?.persist?.().catch(() => undefined);
}

export async function loadLocalFile(id: string): Promise<Blob | undefined> {
  const blob = await tx<Blob | undefined>('readonly', (s) => s.get(keyFor(id)) as IDBRequest<Blob | undefined>);
  if (blob || activeProfile() !== LEGACY) return blob;
  // Files saved before profiles existed have bare ids.
  return tx<Blob | undefined>('readonly', (s) => s.get(id) as IDBRequest<Blob | undefined>);
}

export async function deleteLocalFile(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(keyFor(id)));
}

/** Copies files between profiles (used when a guest's setup is adopted by a new account). */
export async function copyLocalFiles(from: string, to: string, ids: string[]): Promise<void> {
  await Promise.all(
    ids.map(async (id) => {
      const blob = await tx<Blob | undefined>('readonly', (s) => s.get(`${from}/${id}`) as IDBRequest<Blob | undefined>);
      if (blob) await tx('readwrite', (s) => s.put(blob, `${to}/${id}`));
    }),
  );
}

/** Removes this profile's files that are no longer referenced. Other profiles are untouched. */
export async function pruneLocalFiles(keep: Set<string>): Promise<void> {
  const profile = activeProfile();
  const prefix = `${profile}/`;
  const keys = (await tx<IDBValidKey[]>('readonly', (s) => s.getAllKeys())).map(String);
  const stale = keys.filter((k) =>
    k.startsWith(prefix) ? !keep.has(k.slice(prefix.length)) : profile === LEGACY && !k.includes('/') && !keep.has(k),
  );
  await Promise.all(stale.map((k) => tx('readwrite', (s) => s.delete(k))));
}
